import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { wantsPhone } from './consent';
import type { Consent } from './types';

const TOPICS = [
  {
    key: 'news',
    label: 'Informativos do prof. Rodrigo Bossini',
    detail: 'IA, desenvolvimento, nuvem, DevOps e afins',
  },
  { key: 'offers', label: 'Produtos e cursos do prof. Rodrigo Bossini' },
  { key: 'partners', label: 'Produtos e cursos de terceiros' },
] as const;

type Topic = (typeof TOPICS)[number]['key'];

/**
 * The opt-ins (LGPD: all off unless ticked): each topic by e-mail and/or phone. The phone field
 * shows up once a phone option is ticked.
 */
export function ConsentOptions({
  value,
  onChange,
  email,
  phoneError,
  disabled,
  phoneAction,
}: {
  value: Consent;
  onChange: (next: Consent) => void;
  /** The address the e-mails go to (the sign-in one). */
  email: string;
  phoneError?: string;
  disabled?: boolean;
  /** E.g. a "Salvar" button beside the phone field. */
  phoneAction?: React.ReactNode;
}) {
  const toggle = (topic: Topic, channel: 'Email' | 'Phone') => {
    const key = `${topic}${channel}` as const;
    onChange({ ...value, [key]: !value[key] });
  };

  return (
    <Stack spacing={1}>
      {TOPICS.map((topic) => (
        <Stack
          key={topic.key}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0, sm: 2 }}
          sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {topic.label}
            </Typography>
            {'detail' in topic && (
              <Typography variant="caption" color="text.secondary">
                {topic.detail}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <FormControlLabel
              disabled={disabled}
              control={
                <Checkbox
                  size="small"
                  checked={value[`${topic.key}Email`]}
                  onChange={() => toggle(topic.key, 'Email')}
                  slotProps={{ input: { 'aria-label': `${topic.label} por e-mail` } }}
                />
              }
              label={<Typography variant="body2">E-mail</Typography>}
            />
            <FormControlLabel
              disabled={disabled}
              control={
                <Checkbox
                  size="small"
                  checked={value[`${topic.key}Phone`]}
                  onChange={() => toggle(topic.key, 'Phone')}
                  slotProps={{ input: { 'aria-label': `${topic.label} por telefone` } }}
                />
              }
              label={<Typography variant="body2">Telefone</Typography>}
            />
          </Stack>
        </Stack>
      ))}
      {wantsPhone(value) && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', pt: 0.5 }}>
          <TextField
            label="Telefone (WhatsApp)"
            type="tel"
            autoComplete="tel"
            size="small"
            placeholder="(11) 98765-4321"
            value={value.phone ?? ''}
            onChange={(e) => onChange({ ...value, phone: e.target.value })}
            error={Boolean(phoneError)}
            helperText={phoneError ?? 'Com DDD.'}
            disabled={disabled}
            sx={{ flex: 1, maxWidth: 320 }}
          />
          {phoneAction}
        </Stack>
      )}
      <Typography variant="caption" color="text.secondary">
        Os e-mails vão para {email}. Usamos esses contatos só para isso, e você desmarca quando
        quiser em “Novidades e comunicações”, no fim de qualquer página do Lousa.
      </Typography>
    </Stack>
  );
}
